/* eslint-disable react/jsx-props-no-spreading */
import { ConfirmInfo } from '@coop/components';
import classNames from 'classnames';
import type { FC } from 'react';
import React from 'react';

import CloudinaryImage from '../../../../../common/components/atoms/CloudinaryImage';
import RawHtml from '../../../../../common/components/atoms/RawHtml';
import useModal from '../../../../../common/hooks/useModal';
import { useAppSelector } from '../../../../../common/hooks/useThunkDispatch';
import type {
    ClaimReportingInformations,
    ProductAnimalFoodData,
    ProductCatchProps,
    ProductContactInformation,
    ProductFishReportingInformationsProps,
} from '../../../../../common/models/props/itemTeaserProps';
import { featureFlagsSelectors } from '../../../../../common/selectors/featureFlagsSelectors';
import Disclaimer from './Disclaimer';
import ProductDetailHeading from './ProductDetailHeading';
import styles from './ProductDetailsAccordion.module.scss';
import type { AnimalTableRow, ProductFactsProps } from './ProductDetailsAccordion.types';

const getStorageInstructions = (storageInstructions?: string, maxStorageTemperature?: string) => {
    if (storageInstructions) {
        // storage instructions has priority
        return storageInstructions;
    }

    return `Förvaras vid högst ${maxStorageTemperature}${'\xB0'}C`;
};

const FirsReportingInformation = (props: {
    fishReportingInformations?: ProductFishReportingInformationsProps[];
}) => {
    if (!props.fishReportingInformations) return null;

    // check if the fish name is important or we just show all catch methods and areas. Currently always show all.
    const catchInformations =
        props.fishReportingInformations.flatMap((info) => info.catchInformation) || [];

    const catchMethods = catchInformations
        .flatMap((info) => info.catchMethods)
        .filter((method) => !!method.code && !!method.name)
        .map((area) => area.name)
        .join(', ');

    const catchAreas = catchInformations
        .flatMap((info) => info.catchAreas)
        .filter((area) => !!area.code && !!area.name)
        .map((area) => area.name)
        .join(', ');

    return (
        <>
            {catchAreas && (
                <div className="u-marginBmd">
                    <ProductDetailHeading>Fångstområde</ProductDetailHeading>
                    <p className="u-marginTz">{catchAreas}</p>
                </div>
            )}
            {catchMethods && (
                <div className="u-marginBmd">
                    <ProductDetailHeading>Fångstmetod</ProductDetailHeading>
                    <p className="u-marginTz">{catchMethods}</p>
                </div>
            )}
        </>
    );
};

const FishInformations = (props: {
    catchData?: ProductCatchProps;
    fishReportingInformations?: ProductFishReportingInformationsProps[];
}) => {
    const isFishReportingInformationEnabled = useAppSelector(
        featureFlagsSelectors.selectFishReportingInformationEnabled,
    );

    return isFishReportingInformationEnabled ? (
        <FirsReportingInformation fishReportingInformations={props.fishReportingInformations} />
    ) : (
        // old fields that will be removed in the future if the new data is fine
        <>
            {props.catchData?.catchArea && (
                <div className="u-marginBmd">
                    <ProductDetailHeading>Fångstområde</ProductDetailHeading>
                    <p className="u-marginTz">{props.catchData.catchArea}</p>
                </div>
            )}
            {props.catchData?.catchMethod && (
                <div className="u-marginBmd">
                    <ProductDetailHeading>Fångstmetod</ProductDetailHeading>
                    <p className="u-marginTz">{props.catchData.catchMethod}</p>
                </div>
            )}
        </>
    );
};

const ProductFacts: FC<React.PropsWithChildren<ProductFactsProps>> = (props) => {
    // list of non food ingredients should only be shown if the default list is empty
    const listOfIngredients = props.listOfIngredients || props.listOfNonFoodIngredients;

    const countryOfOriginConfirm = useModal('CountryOfOrigin');

    const healthSafetySignalWordsCode = props.healthSafetySignalWordsCode?.[0];

    return (
        <>
            {(!!props.healthattributeLabels?.length || !!healthSafetySignalWordsCode) && (
                <div className="u-marginBmd">
                    <div className="u-flex u-flexAlignCenter u-flexGap8 u-flexWrap u-marginBxxsm">
                        {!!props.healthattributeLabels?.length && (
                            <ul
                                aria-label="Faropiktogram"
                                className="u-flex u-flexAlignCenter u-flexGap8 u-flexWrap u-listStyleTypeNone u-paddingAz u-marginAz"
                            >
                                {props.healthattributeLabels.map((label) => (
                                    <li
                                        key={`${label.identifier}-${label.image?.identifier ?? ''}`}
                                    >
                                        <CloudinaryImage
                                            url={label.image?.url || ''}
                                            name={label.name}
                                            title={label.name}
                                            transform="q_auto,f_auto,w_48"
                                            retinaTransform="q_auto,f_auto,w_96"
                                            width={48}
                                            height={48}
                                        />
                                    </li>
                                ))}
                            </ul>
                        )}
                        {healthSafetySignalWordsCode && (
                            <p className="u-textWeightBold">{healthSafetySignalWordsCode}</p>
                        )}
                    </div>

                    {props.healthSafetySignalStatementText?.map((signalStatement) => (
                        <p className="u-marginVxxsm" key={signalStatement}>
                            {signalStatement}
                        </p>
                    ))}
                </div>
            )}
            {!!props.healthSafetyPrecautionaryStatementText?.length && (
                <div className="u-marginBmd">
                    <ProductDetailHeading>Skyddsangivelse</ProductDetailHeading>
                    {props.healthSafetyPrecautionaryStatementText.map((precautionaryStatement) => (
                        <p className="u-marginVxxsm" key={precautionaryStatement}>
                            {precautionaryStatement}
                        </p>
                    ))}
                </div>
            )}
            {(listOfIngredients ||
                props.storageState?.code === 'PREVIOUSLY_FROZEN' ||
                props.claimReportingInformations) && (
                <div className="u-marginBmd">
                    {props.regulatedArticleDescription && (
                        <div className="u-marginBxxsm">{props.regulatedArticleDescription}</div>
                    )}
                    <ProductDetailHeading>Innehåll</ProductDetailHeading>
                    {listOfIngredients && <RawHtml html={listOfIngredients} />}
                    {props.storageState?.code === 'PREVIOUSLY_FROZEN' && (
                        <div className={classNames(listOfIngredients && 'u-marginTxsm')}>
                            {props.storageState?.value}
                        </div>
                    )}
                    <ClaimReportingInformation
                        claimReportingInformations={props.claimReportingInformations}
                    />
                </div>
            )}

            <AnimalFoodInfo animalFoodData={props.animalFoodData} />
            {(props.consumerInstructions?.usageInstructions || props.compulsoryAdditiveLabel) && (
                <div className="u-marginBmd">
                    <ProductDetailHeading>Dosering & användning</ProductDetailHeading>
                    {props.consumerInstructions?.usageInstructions && (
                        <p className="u-marginTz">{props.consumerInstructions.usageInstructions}</p>
                    )}
                    {props.compulsoryAdditiveLabel && (
                        <p className="u-marginTz">{props.compulsoryAdditiveLabel}</p>
                    )}
                </div>
            )}
            <AnimalFoodTable animalFoodTable={props.animalFoodData?.animalFeedingTable} />
            {!!props.preparationInstructionsList.length && (
                <div className="u-marginBmd">
                    <ProductDetailHeading>Beredning</ProductDetailHeading>
                    <div className={styles.PreparationInstructions}>
                        {props.preparationInstructionsList.map((pi) => (
                            <p className={styles.PreparationInstructionsParagraph} key={pi}>
                                {pi}
                            </p>
                        ))}
                    </div>
                </div>
            )}
            {(props.maxStorageTemperature || props.consumerInstructions?.storageInstructions) && (
                <div className="u-marginBmd">
                    <ProductDetailHeading>Förvaring</ProductDetailHeading>
                    <p className="u-marginTz">
                        {getStorageInstructions(
                            props.consumerInstructions?.storageInstructions,
                            props.maxStorageTemperature,
                        )}
                    </p>
                </div>
            )}
            {props.declarationOfOrigin && (
                <div className="u-marginBmd">
                    <ProductDetailHeading>Ursprung</ProductDetailHeading>
                    <p className="u-marginTz">{props.declarationOfOrigin}</p>
                </div>
            )}
            {!!props.replacementCountries?.length && (
                <div className="u-marginBmd">
                    <ProductDetailHeading>Möjliga ersättningsländer</ProductDetailHeading>
                    <p className="u-marginTz">
                        {props.replacementCountries.map((country) => country.value).join(', ')}
                    </p>
                </div>
            )}
            <FishInformations
                catchData={props.catchData}
                fishReportingInformations={props.fishReportingInformations}
            />
            {!!props.countriesOfOrigin?.length && (
                <div className="u-marginBmd">
                    <ConfirmInfo
                        show={countryOfOriginConfirm.isOpen}
                        close={countryOfOriginConfirm.close}
                        heading="Tillverkningsland"
                        message="Artikelns tillverkningsland är det land där den genomgick sin sista
                            väsentliga bearbetning och/eller förpackades."
                        renderTrigger={(ref) => (
                            <ProductDetailHeading>
                                <button
                                    className="u-textUnderline u-outlineSolidBase2"
                                    ref={ref}
                                    type="button"
                                    onClick={countryOfOriginConfirm.open}
                                >
                                    Tillverkningsland
                                </button>
                            </ProductDetailHeading>
                        )}
                    />
                    <p className="u-marginTz">{props.countriesOfOrigin.join(', ')}</p>
                </div>
            )}
            {props.eanCode && (
                <div className="u-marginBmd">
                    <ProductDetailHeading>Övrigt</ProductDetailHeading>
                    <p className="u-marginTz">EAN kod: {props.eanCode}</p>
                </div>
            )}
            <ContactInformation contactInformation={props.contactInformation} />
            <Disclaimer />
        </>
    );
};

const ContactInformation = (props: { contactInformation?: ProductContactInformation[] }) => {
    const isContactInformationEnabled = useAppSelector(
        featureFlagsSelectors.selectContactInformationEnabled,
    );

    const contactInformations =
        props.contactInformation?.map(({ typeName, name, address, channels }) => ({
            header: typeName,
            info: `${name}, ${address}`,
            channels: Object.values(
                channels.reduce<
                    Record<string, { displayName: string; value: string; code: string }>
                >((acc, { code, displayName, value }) => {
                    acc[code] = acc[code]
                        ? { ...acc[code], value: `${acc[code].value}, ${value}` }
                        : { code, displayName, value };
                    return acc;
                }, {}),
            ),
        })) || [];

    return (
        isContactInformationEnabled &&
        contactInformations.map((contact) => (
            <div className="u-marginBmd" key={contact.header}>
                <ProductDetailHeading>{contact.header}</ProductDetailHeading>
                <p className="u-marginTz u-marginBxxxsm">{contact.info}</p>
                {contact.channels.map((channel) => (
                    <p className="u-marginTz u-marginBxxxsm" key={channel.code}>
                        {channel.displayName}: {channel.value}
                    </p>
                ))}
            </div>
        ))
    );
};

const AnimalFoodTable = (props: {
    animalFoodTable?: ProductAnimalFoodData['animalFeedingTable'];
}) => {
    if (!props.animalFoodTable) return null;

    const rows = props.animalFoodTable.filter(
        (row): row is AnimalTableRow =>
            !!row.animalWeight?.displayName && !!row.guidingAmount?.displayName,
    );

    if (rows.length === 0) return null;

    return (
        <div>
            <div className={styles.AnimalFoodTable}>
                <div className={classNames(styles.HeaderCell, styles.Cell)}>Djurets vikt</div>
                <div className={classNames(styles.HeaderCell, styles.Cell)}>Vägledande mängd</div>
                {rows.map((row, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <React.Fragment key={`${row.animalWeight.displayName}_${index}`}>
                        <div className={classNames(styles.ContentCell, styles.Cell)}>
                            {row.animalWeight.displayName.toLowerCase()}
                        </div>
                        <div className={classNames(styles.ContentCell, styles.Cell)}>
                            {row.guidingAmount.displayName.toLowerCase()}
                        </div>
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};

const AnimalFoodInfo = (props: { animalFoodData?: ProductAnimalFoodData }) => {
    if (!props.animalFoodData) return null;

    return (
        <>
            {props.animalFoodData?.targetedConsumptionBy && (
                <div className="u-marginBmd">
                    <ProductDetailHeading>Avsedd för</ProductDetailHeading>
                    <p className="u-marginTz">{props.animalFoodData.targetedConsumptionBy.value}</p>
                </div>
            )}
            {props.animalFoodData?.feedType && (
                <div className="u-marginBmd">
                    <ProductDetailHeading>Fodertyp</ProductDetailHeading>
                    <p className="u-marginTz">{props.animalFoodData.feedType}</p>
                </div>
            )}
            {props.animalFoodData?.feedCompositionStatement && (
                <div className="u-marginBmd">
                    <ProductDetailHeading>Sammansättning</ProductDetailHeading>
                    <p className="u-marginTz">{props.animalFoodData.feedCompositionStatement}</p>
                </div>
            )}
            {props.animalFoodData?.feedAnalyticalConstituentsStatement && (
                <div className="u-marginBmd">
                    <ProductDetailHeading>Analytiska beståndsdelar</ProductDetailHeading>
                    <p className="u-marginTz">
                        {props.animalFoodData.feedAnalyticalConstituentsStatement}
                    </p>
                </div>
            )}
            {props.animalFoodData?.feedAdditiveStatement && (
                <div className="u-marginBmd">
                    <ProductDetailHeading>Tillsatser</ProductDetailHeading>
                    <p className="u-marginTz">{props.animalFoodData.feedAdditiveStatement}</p>
                </div>
            )}
            {props.animalFoodData?.feedingInstructions && (
                <div className="u-marginBmd">
                    <ProductDetailHeading>Utfodringsinstruktioner</ProductDetailHeading>
                    <p className="u-marginTz">{props.animalFoodData.feedingInstructions}</p>
                </div>
            )}
        </>
    );
};

const ClaimReportingInformation = ({
    claimReportingInformations,
}: {
    claimReportingInformations?: ClaimReportingInformations;
}) => {
    if (!claimReportingInformations) return null;

    return (
        <>
            {claimReportingInformations?.description?.map((description) => (
                <p className="u-marginTxxsm u-marginBz">{description.value}</p>
            ))}
            {claimReportingInformations?.claimDetails?.map((claim) => (
                <p className="u-marginTxxsm u-marginBz">
                    {claim.name} {claim.elements.map((element) => element.name).join(', ')}
                </p>
            ))}
        </>
    );
};

export default ProductFacts;
